import {
  CopyAllOutlined,
  DeleteOutline,
  Download,
  Edit,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  formatSkillDate,
  getSkillAuthorName,
  skillVisibilityChipColorByVisibility,
  skillVisibilityIconByVisibility,
  skillVisibilityLabel,
} from "../../skill-utils";
import {
  isSkillFormValid,
  skillFormDescriptionByMode,
} from "../../SkillFormModal";
import type {
  SavedSkillActions,
  SkillDetailForm,
  SkillDetailSelection,
} from "../types";
import { SkillSettingsMenu } from "./SkillSettingsMenu";

type SkillDetailHeaderProps = {
  selection: SkillDetailSelection;
  form: SkillDetailForm;
  savedSkillActions: SavedSkillActions;
};

export const SkillDetailHeader = ({
  selection,
  form,
  savedSkillActions,
}: SkillDetailHeaderProps) => {
  const { skillId, skill, isLoading } = selection;
  const {
    mode: formMode,
    values: formValues,
    initialValues: formInitialValues,
    isSaving: isSavingSkill,
    onCancel: onCancelForm,
    onSave: onSaveForm,
  } = form;
  const isFormHeader = !!formMode && !!formValues;
  const hasSelectedSkill = !!skillId && !!skill;
  const detailActionsDisabled = isLoading || !hasSelectedSkill;
  const selectedSkillAuthorName = skill ? getSkillAuthorName(skill) : null;
  const SelectedSkillVisibilityIcon = skill
    ? skillVisibilityIconByVisibility[skill.visibility]
    : null;

  return (
    <Box
      sx={{
        py: 0.875,
        px: 1.3125,
        borderBottom: 1,
        borderColor: "divider",
      }}
      data-testid={isFormHeader ? "skills-form-header" : "skills-detail-header"}
    >
      <Stack gap={0.75}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          data-testid="skills-detail-header-title-row"
        >
          {isFormHeader ? (
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1 }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                noWrap
                sx={{ minWidth: 0 }}
              >
                {formValues.name.trim() ||
                  formInitialValues?.name?.trim() ||
                  "untitled-skill"}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="h5"
              fontWeight={700}
              noWrap
              sx={{ minWidth: 0, flex: 1 }}
            >
              {skill?.name}
            </Typography>
          )}
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            data-testid="skills-detail-header-actions"
            sx={{
              flexShrink: 0,
              ml: "auto",
              mr: 0,
            }}
          >
            {isFormHeader ? (
              <>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={onCancelForm}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => onSaveForm(formValues)}
                  disabled={!isSkillFormValid(formValues) || isSavingSkill}
                  startIcon={
                    isSavingSkill ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  Save
                </Button>
              </>
            ) : null}
            <Tooltip
              title={
                skill?.canEdit
                  ? "Edit"
                  : skill
                    ? "Global and shared skills must be duplicated before editing"
                    : "Select a saved skill to edit"
              }
            >
              <span>
                <IconButton
                  aria-label="Edit"
                  size="small"
                  onClick={savedSkillActions.onEdit}
                  disabled={detailActionsDisabled || !skill?.canEdit}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Duplicate">
              <span>
                <IconButton
                  aria-label="Duplicate"
                  size="small"
                  onClick={savedSkillActions.onDuplicate}
                  disabled={detailActionsDisabled}
                >
                  <CopyAllOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download .md">
              <span>
                <IconButton
                  aria-label="Download .md"
                  size="small"
                  onClick={() => skill && savedSkillActions.onDownload(skill)}
                  disabled={detailActionsDisabled}
                >
                  <Download fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <SkillSettingsMenu
              skill={hasSelectedSkill ? skill : undefined}
              user={savedSkillActions.user}
              detailLoading={isLoading}
              patchPending={savedSkillActions.isSettingsSaving}
              closeKey={skillId}
              onRequestOpen={savedSkillActions.onRequestOpenSettings}
              onToggleEnabled={savedSkillActions.onToggleEnabled}
              onToggleForceEnabled={savedSkillActions.onToggleForceEnabled}
            />
            <Tooltip
              title={
                skill?.canDelete
                  ? "Delete"
                  : skill
                    ? "Only the creator can delete this skill"
                    : "Select a saved skill to delete"
              }
            >
              <span>
                <IconButton
                  aria-label="Delete"
                  size="small"
                  edge="end"
                  onClick={savedSkillActions.onDelete}
                  disabled={detailActionsDisabled || !skill?.canDelete}
                  sx={{
                    marginRight: "-6.5px",
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        {isFormHeader ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="skills-form-header-meta"
          >
            {skillFormDescriptionByMode[formMode]}
          </Typography>
        ) : skill ? (
          <Stack gap={0.75} data-testid="skills-detail-header-meta">
            <Stack
              direction="row"
              gap={1.5}
              alignItems="center"
              flexWrap="wrap"
              data-testid="skills-detail-header-meta-text"
            >
              {selectedSkillAuthorName ? (
                <Typography variant="body2" color="text.secondary">
                  Created by {selectedSkillAuthorName}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Updated {formatSkillDate(skill.updatedAt)}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              gap={0.75}
              alignItems="center"
              flexWrap="wrap"
              data-testid="skills-detail-header-chips"
            >
              {SelectedSkillVisibilityIcon ? (
                <Chip
                  icon={<SelectedSkillVisibilityIcon fontSize="small" />}
                  label={skillVisibilityLabel[skill.visibility]}
                  color={skillVisibilityChipColorByVisibility[skill.visibility]}
                  variant="outlined"
                  size="xxs"
                />
              ) : null}
              {!skill.enabled ? (
                <Chip
                  label="Disabled for me"
                  color="default"
                  variant="outlined"
                  size="xxs"
                />
              ) : null}
              {skill.forceEnabled ? (
                <Chip
                  label="Force Enabled System Wide"
                  color="error"
                  variant="outlined"
                  size="xxs"
                />
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};
